import {useMemo} from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Card, CardActions, CardHeader} from "../components/layouts/card-components.tsx";
import {InputText} from "../components/input-text.tsx";
import * as yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";

import InputSelectForm from "../components/input-select-form.tsx";
import countries from "../lib/countries.json"
import i18n from "../i18n.ts";
import {API} from "../lib/axios.ts";
import {useIsMobile} from "../hooks/is-mobile.tsx";
import {useAppContext} from "../context/app-context.tsx";
import {fetchNetwork} from "../helpers/async-calls.ts";


type FormValues = {
    name: string;
    email: string
    country: string
    first_name?: string
    last_name?: string
    language: string
};
type Company = {
    name: string;
    email: string;
    country: string
    first_name?: string
    last_name?: string
    language: string

}
export const AddCompany = () => {
    const {t} = useTranslation();
    const {user, setNetwork} = useAppContext()
    const language = i18n.language;
    const isMobile = useIsMobile()

    // Every message here is an ERROR, not a field label. Three of the four used
    // to be neither: `email` borrowed the product-code message ("Product code
    // is required."), and country/language passed their own LABEL keys, so a
    // missing country read simply "Country". `email` also had no `.email()`
    // rule at all, so any string passed and the address only failed server-side
    // — after the company had already been submitted.
    //
    // Memoised on `t`, which react-i18next replaces on every language change —
    // so the messages follow the active language without the schema being
    // rebuilt on every keystroke.
    const schema = useMemo(() => yup.object({
        name: yup.string()
            .required(t('create_company.errors.name_required', 'Company name is required.')),
        email: yup.string()
            .email(t('create_company.errors.email_invalid', 'Enter a valid email address.'))
            .required(t('create_company.errors.email_required', 'Email is required.')),
        country: yup.string()
            .required(t('create_company.errors.country_required', 'Country is required.')),
        language: yup.string()
            .required(t('create_company.errors.language_required', 'Language is required.')),
    }), [t]);

    const methods = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            language: user?.company.language
        }});

    const {register, handleSubmit, formState: {errors}} = methods;

    const options = countries.map((country) => ({
        value: country.alpha2,
        label: country[language as keyof typeof country],
    }));
    // Kept in step with the languages i18n.ts actually ships. German was
    // missing here, so a German-language company simply could not be created.
    const languages = [
        {value: "en", label: t("global.language.english")},
        {value: "sk", label: t("global.language.slovak")},
        {value: "cs", label: t("global.language.czech")},
        {value: "de", label: t("global.language.german")},
        {value: "hu", label: t("global.language.hungarian")},
        {value: "it", label: t("global.language.italian")},
        {value: "pl", label: t("global.language.polish")},
        {value: "ro", label: t("global.language.romanian")}
    ];

    const createCompany = async (company: Company) => {
        try {
            const {data: response} = await API.post("/companies", {
                ...company,
                franchise: true,
            });
            fetchNetwork().then((network) => {
                if (network) {
                    setNetwork(network);
                }
            });
            return response;
        } catch (e) {
            console.error("⚠️ Failed to create company", e);
        }
    };

    const onSubmit = (data: any) => {
        createCompany(data);
    };

    return (
        <div className="z2 app_page_layout support_wide_screens">
            <div className="z2 app_page_toolbar">
                <div className="buttons">
                    <Link to={"/companies"} role="button" className="z2">
                        {t("nav.label.back")}
                    </Link>
                </div>
            </div>
            <div className={"app_page_body"}>
                <div style={{gridColumn: "span 4"}}>
                    <Card>
                        <CardHeader title={t("add_company.title.add_company")}/>

                        <FormProvider {...methods}>
                            {/* noValidate: the inputs carry the native `required` attribute for its
                                ACCESSIBILITY meaning, but the browser's own constraint UI would
                                otherwise block submit before react-hook-form's resolver ran — so the
                                yup messages below never reached the screen, and what the user saw
                                instead was a native tooltip in the BROWSER's language, not the app's.
                                Validation belongs to the resolver; `required` stays for screen readers. */}
                            <form className="card_form" noValidate onSubmit={handleSubmit(onSubmit)}>
                                <InputText
                                    label={t('crete_company.label.company_name')}
                                    required
                                    register={register('name')}
                                    error={errors.name}
                                    name={"name"}
                                />
                                <InputText
                                    label={t('crete_company.label.email')}
                                    required
                                    register={register('email')}
                                    error={errors.email}
                                    name={"email"}
                                />
                                <InputSelectForm
                                    name="country"
                                    label={t("crete_company.label.country")}
                                    options={options}
                                    optionsText="label"
                                    optionsValue="value"
                                    required
                                />
                                <InputSelectForm
                                    name="language"
                                    label={t("crete_company.label.language")}
                                    options={languages}
                                    optionsText="label"
                                    optionsValue="value"
                                    required
                                />

                                <div className={"card_row_list_item"} style={{
                                    gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(2, 1fr)",
                                    gap: 20
                                }}>
                                    <div className="list_item_label" style={{padding: 0}}>
                                        <InputText
                                            label={t('crete_company.label.first_name')}
                                            register={register('first_name')}
                                            error={errors.first_name}
                                            name={"first_name"}
                                        />
                                    </div>
                                    <div className="list_item_value" style={{padding: 0}}>
                                        <InputText
                                            label={t('crete_company.label.last_name')}
                                            register={register('last_name')}
                                            error={errors.last_name}
                                            name={"last_name"}
                                        />
                                    </div>

                                </div>


                                <CardActions>
                                    <button type="submit" className="z2">
                                        {t("create_company.button.create_company")}
                                    </button>
                                </CardActions>
                            </form>
                        </FormProvider>
                    </Card>
                </div>
            </div>
        </div>
    );
}
    ;
